import React from 'react';

const TimelineModal = ({ 
  isOpen, 
  onClose, 
  props, 
  onPeriodSelect 
}) => {
  if (!isOpen) return null;

  // Historical Timeline Data and Logic
  const timelinePeriods = [
    {
      name: 'Ancient',
      period: 'Ancient',
      yearRange: '3000 BCE - 500 CE',
      startYear: -3000,
      endYear: 500,
      color: '#8B4513',
      description: 'Ancient civilizations, classical antiquity',
      keywords: ['ancient', 'classical', 'greek', 'roman', 'egyptian', 'bronze age', 'iron age']
    },
    {
      name: 'Medieval',
      period: 'Medieval',
      yearRange: '500 - 1500 CE',
      startYear: 500,
      endYear: 1500,
      color: '#4A5D23',
      description: 'Middle Ages, Gothic, Romanesque',
      keywords: ['medieval', 'middle ages', 'gothic', 'romanesque', 'feudal', 'crusades']
    },
    {
      name: 'Renaissance',
      period: 'Renaissance',
      yearRange: '1400 - 1600',
      startYear: 1400,
      endYear: 1600,
      color: '#B8860B',
      description: 'Renaissance, humanism, artistic rebirth',
      keywords: ['renaissance', 'elizabethan', 'tudor', 'baroque early']
    },
    {
      name: 'Baroque',
      period: 'Baroque',
      yearRange: '1600 - 1750',
      startYear: 1600,
      endYear: 1750,
      color: '#8B0000',
      description: 'Baroque, ornate, dramatic style',
      keywords: ['baroque', 'ornate', 'dramatic', 'rococo early']
    },
    {
      name: 'Colonial',
      period: 'Colonial',
      yearRange: '1600 - 1776',
      startYear: 1600,
      endYear: 1776,
      color: '#2F4F4F',
      description: 'American Colonial period',
      keywords: ['colonial', 'american colonial', 'pilgrim', 'puritan']
    },
    {
      name: 'Georgian',
      period: 'Georgian',
      yearRange: '1714 - 1830',
      startYear: 1714,
      endYear: 1830,
      color: '#483D8B',
      description: 'Georgian era, neoclassical',
      keywords: ['georgian', 'neoclassical', 'regency']
    },
    {
      name: 'Victorian',
      period: 'Victorian',
      yearRange: '1837 - 1901',
      startYear: 1837,
      endYear: 1901,
      color: '#800080',
      description: 'Victorian era, Industrial Revolution',
      keywords: ['victorian', 'industrial', 'steam age', 'gothic revival']
    },
    {
      name: 'Edwardian',
      period: 'Edwardian',
      yearRange: '1901 - 1914',
      startYear: 1901,
      endYear: 1914,
      color: '#708090',
      description: 'Edwardian era, Belle Époque',
      keywords: ['edwardian', 'belle epoque', 'art nouveau']
    },
    {
      name: 'Art Deco',
      period: 'Art Deco',
      yearRange: '1920 - 1940',
      startYear: 1920,
      endYear: 1940,
      color: '#FFD700',
      description: 'Art Deco, Jazz Age, modernism',
      keywords: ['art deco', 'jazz age', '1920s', '1930s', 'modernist']
    },
    {
      name: 'Mid-Century',
      period: 'Mid-Century Modern',
      yearRange: '1945 - 1970',
      startYear: 1945,
      endYear: 1970,
      color: '#FF6347',
      description: 'Mid-century modern, post-war design',
      keywords: ['mid-century', 'atomic age', '1950s', '1960s', 'space age']
    },
    {
      name: 'Contemporary',
      period: 'Contemporary',
      yearRange: '1970 - Present',
      startYear: 1970,
      endYear: 2024,
      color: '#4169E1',
      description: 'Contemporary, modern, current',
      keywords: ['contemporary', 'modern', 'current', '1980s', '1990s', '2000s']
    }
  ];

  const getTimelinePropCounts = () => {
    const periodCounts = {};
    
    timelinePeriods.forEach(period => {
      periodCounts[period.name] = 0;
    });
    
    props.forEach(prop => {
      const searchText = [
        prop.historical_period,
        prop.name,
        ...(prop.tags || []),
        prop.category
      ].join(' ').toLowerCase();
      
      timelinePeriods.forEach(period => {
        if (period.keywords.some(keyword => searchText.includes(keyword))) {
          periodCounts[period.name]++;
        }
      });
    });
    
    return periodCounts;
  };

  const handlePeriodSelect = (period) => {
    onPeriodSelect(period);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold mb-2">📅 Historical Era Timeline</h2>
              <p className="text-green-100">Explore props by historical period. Click any era to find matching items.</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-green-200 text-2xl"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Timeline Content */}
        <div className="p-8 overflow-y-auto max-h-[75vh]">
          {/* Timeline Visual */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-200 via-green-200 to-blue-200"></div>
            
            {/* Timeline Periods */}
            <div className="space-y-6">
              {timelinePeriods.map((period, index) => {
                const propCounts = getTimelinePropCounts();
                const count = propCounts[period.name] || 0;
                
                return (
                  <div key={period.name} className="relative flex items-center">
                    {/* Timeline Dot */}
                    <div 
                      className="relative z-10 w-16 h-16 rounded-full border-4 border-white shadow-lg flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110"
                      style={{ backgroundColor: period.color }}
                      onClick={() => handlePeriodSelect(period)}
                    >
                      <div className="text-white font-bold text-xs text-center leading-tight">
                        {period.name.length > 8 ? period.name.slice(0, 8) + '...' : period.name}
                      </div>
                    </div>
                    
                    {/* Period Card */}
                    <div 
                      className="ml-6 flex-1 bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                      style={{ borderLeftColor: period.color, borderLeftWidth: '4px' }}
                      onClick={() => handlePeriodSelect(period)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">{period.name}</h3>
                          <div className="text-sm text-gray-600 mb-2">{period.yearRange}</div>
                          <p className="text-sm text-gray-700 mb-3">{period.description}</p>
                          
                          {/* Keywords */}
                          <div className="flex flex-wrap gap-1">
                            {period.keywords.slice(0, 4).map(keyword => (
                              <span 
                                key={keyword}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium"
                              >
                                {keyword}
                              </span>
                            ))}
                            {period.keywords.length > 4 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                                +{period.keywords.length - 4} more
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Prop Count */}
                        <div className="text-right ml-4">
                          <div className="text-2xl font-bold" style={{ color: period.color }}>
                            {count}
                          </div>
                          <div className="text-xs text-gray-500">
                            {count === 1 ? 'prop' : 'props'}
                          </div>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      {count > 0 && (
                        <div className="mt-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="h-2 rounded-full transition-all duration-500"
                              style={{ 
                                backgroundColor: period.color,
                                width: `${Math.min((count / Math.max(...Object.values(propCounts))) * 100, 100)}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Era Statistics */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-4">📊 Props by Historical Period:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(getTimelinePropCounts())
                .filter(([period, count]) => count > 0)
                .sort(([,a], [,b]) => b - a)
                .map(([periodName, count]) => {
                  const period = timelinePeriods.find(p => p.name === periodName);
                  return (
                    <div key={periodName} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                      <div 
                        className="w-6 h-6 rounded-full flex-shrink-0"
                        style={{ backgroundColor: period?.color }}
                      ></div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{period?.name}</div>
                        <div className="text-sm text-gray-600">{period?.yearRange}</div>
                      </div>
                      <div className="font-bold text-lg" style={{ color: period?.color }}>
                        {count}
                      </div>
                    </div>
                  );
                })}
            </div>
            
            {/* No data message */}
            {Object.values(getTimelinePropCounts()).every(count => count === 0) && (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-4">📅</div>
                <p className="text-lg font-medium mb-2">No historical period data found</p>
                <p className="text-sm">Add historical period information to props to see timeline data</p>
              </div>
            )}
          </div>
          
          {/* Usage Tips */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">💡 How the timeline works:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Click any period dot or card to search for props from that era</li>
              <li>• Searches prop names, tags, categories, and historical period fields</li>
              <li>• Each period includes related keywords (e.g., "Victorian" finds "industrial", "steam age")</li>
              <li>• Bar charts show relative frequency of props from each period</li>
              <li>• Timeline spans from Ancient civilizations to Contemporary design</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineModal;